import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merges Tailwind class names, resolving any conflicts.
 *
 * @param inputs - An array of class names to merge.
 * @returns A string of merged and optimized class names.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

type StateClassName<State> = string | ((state: State) => string | undefined) | undefined;

/**
 * Like `cn`, but preserves Base UI's `className` as a function of component
 * state. `cn`/clsx silently drop functions, so wrappers doing
 * `cn(base, className)` break `className={(state) => ...}` consumers.
 *
 * @param base - The wrapper's own classes.
 * @param className - The consumer-provided className (string or state function).
 * @returns A string when possible, otherwise a state function Base UI resolves.
 */
export function cnState<State>(
  base: ClassValue,
  className: StateClassName<State>,
): string | ((state: State) => string) {
  if (typeof className === "function") {
    return (state) => cn(base, className(state));
  }
  return cn(base, className);
}
